import React, { memo, useState, useEffect, useMemo } from "react";
import { Layout, Typography, Button, Drawer } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  logout,
  setSelectedGroupMember,
} from "../../../../redux/auth/authSlice";

import { ReactComponent as Logo } from "../../../../assets/svgs/sidebarlogo.svg";
import { ReactComponent as FooterIcon } from "../../../../assets/images/footericon.svg";
import { getApi, postApi } from "../../../../api/api_instance";
import { API_URLS } from "../../../../api/apiUrls";
import { AiOutlineAppstore } from "react-icons/ai";
import { MdOutlineDashboardCustomize } from "react-icons/md";
import { CgSupport } from "react-icons/cg";
import { GoQuestion } from "react-icons/go";
import { RiUserCommunityLine, RiOrganizationChart } from "react-icons/ri";
import "./sidebar.css";

const { Sider } = Layout;

// Menu Items Configuration
const MENU_ITEMS = [
  {
    key: "/organization/dashboard",
    label: "Dashboard",
    icon: <AiOutlineAppstore className="w-[17px] h-[17px]" />,
    children: [
      {
        key: "/organization/dashboard/emission-tracking",
        label: "Emission Tracking",
      },
      {
        key: "/organization/dashboard/environmental-impact",
        label: "Environmental Impact",
      },
      { key: "/organization/dashboard/social", label: "Social" },
      { key: "/organization/dashboard/governance", label: "Governance" },
      { key: "/organization/dashboard/supply-chain", label: "Supply Chain" },
      { key: "/organization/dashboard/sdg", label: "SDG" },
    ],
  },
  {
    key: "/organization/user-management",
    label: "User Management",
    icon: <RiUserCommunityLine className="w-[17px] h-[17px]" />,
    children: [
      {
        key: "/organization/user-management/roles-and-permissions",
        label: "Roles and Permissions",
      },
      { key: "/organization/user-management/profile", label: "My Profile" },
      {
        key: "/organization/user-management/license-access",
        label: "License & Access",
      },
      // {
      //   key: "/organization/user-management/location-user",
      //   label: "Location User",
      // },
    ],
  },
  {
    key: "/organization/projects",
    label: "Project",
    icon: <MdOutlineDashboardCustomize className="w-[17px] h-[17px]" />,
    children: [
      {
        key: "/organization/projects/standard-forms",
        label: "Standard Form",
      },
      {
        key: "/organization/projects/dynamic-forms",
        label: "Dynamics Form",
      },
    ],
  },
  // {
  //   key: "/organization/organization/Department",
  //   label: "Location",
  // },
  // {
  //   key: "/organization/my-question-bank",
  //   label: "My Question Bank",
  //   icon: <GoQuestion className="w-[17px] h-[17px]" />,
  // },
  {
    key: "/organization/organization-setup",
    label: "Organization Setup",
    icon: <RiOrganizationChart className="w-[17px] h-[17px]" />,
  },
  // {
  //   key: "/organization/manage-forms",
  //   label: "Manage Forms",
  //   icon: <GrFormCalendar className="w-5 h-5" />,
  // },

  // {
  //   key: "/organization/assigned-form",
  //   label: "Assigned Form",
  //   icon: <GrFormCalendar className="w-5 h-5" />,
  // },
  {
    key: "/organization/support",
    label: "Support",
    icon: <CgSupport className="w-[17px] h-[17px]" />,
  },
  // {
  //   key: "/organization/footprint",
  //   label: "Individual footprint calculator",
  //   icon: <FaCalculator className="w-4 h-4" />,
  // },
];

const MODULE_KEY_MAP = {
  Dashboard: "dashbaord",
  "Emission Tracking": "emission-tracking",
  "Environmental Impact": "environmental-impact",
  Social: "social",
  Governance: "governance",
  "Supply Chain": "supply-chain",
  SDG: "sdg",

  Project: "project",
  "Standard Form": "standard_form",
  "Dynamics Form": "dynamic-forms",

  "User Management": "usermanagement",
  "Roles and Permissions": "roles-and-permissions",
  "My Profile": "profile",
  "License & Access": "license-access",
  "Location User": "location-user",

  "My Question Bank": "question-bank",
  "Organization Setup": "org-setup",
  "Manage Forms": "manage-forms",
  "Assigned Form": "assigned-form",
  Support: "support",
};

// Component for Sidebar Logo Header

const SidebarLogo = memo(({ isMobile }) => (
  <div className="flex justify-center items-center overflow-hidden">
    <Logo
      className="sidebarlogo block"
      style={{
        width: isMobile ? "32px" : "200px",
        height: "auto",
        display: "block",
      }}
      preserveAspectRatio="xMidYMid meet"
    />
  </div>
));

// Premium Minimalist Sidebar Menu Component (Ultra Compact, Simple Indent)
const SidebarMenuCustom = memo(
  ({ items, location, onMenuClick, onParentClick }) => {
    return (
      <div className="flex flex-col gap-0.5">
        {items.map((item) => {
          const isParentActive =
            location.pathname === item.key ||
            (item.children &&
              item.children.some((child) => child.key === location.pathname)) ||
            location.pathname.startsWith(item.key);

          return (
            <div key={item.key} className="flex flex-col">
              {/* Parent Item Row */}
              <div
                onClick={() => {
                  if (item.children) {
                    onParentClick(item.key);
                  } else {
                    onMenuClick({ key: item.key });
                  }
                }}
                className={`flex items-center gap-3 px-3 py-1.5 rounded-lg cursor-pointer transition-all duration-150 ease-in-out group ${
                  isParentActive
                    ? "bg-[#06513D]/[0.05] text-[#06513D] font-semibold"
                    : "text-[#000000] hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                {/* Dynamic Icon */}
                <div
                  className={`transition-colors duration-150 ${
                    isParentActive
                      ? "text-[#06513D]"
                      : "text-[#000000] group-hover:text-slate-900"
                  }`}
                >
                  {item.icon}
                </div>

                {/* Text Label */}
                <span className="text-[13.5px] font-medium leading-none">
                  {item.label}
                </span>
              </div>

              {/* Submenu Children - Simple Indent with Bullet Dots */}
              {item.children && (
                <div className="flex flex-col gap-0.5 mt-0.5">
                  {item.children.map((child) => {
                    const isActive = location.pathname === child.key;
                    return (
                      <div
                        key={child.key}
                        onClick={() => onMenuClick({ key: child.key })}
                        className={`flex items-center gap-2 py-1.5 pl-10 pr-3 rounded-lg cursor-pointer text-[13px] font-medium transition-all duration-150 ease-in-out ${
                          isActive
                            ? "text-[#06513D] font-semibold bg-[#E8F5F125]"
                            : "text-[#000000] hover:text-slate-900 hover:bg-slate-50/50"
                        }`}
                      >
                        <span
                          className={`text-[14px] leading-none ${isActive ? "text-[#06513D]" : "text-[#000000]"}`}
                        >
                          •
                        </span>
                        <span>{child.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  },
);

// Sidebar Content Component
const SidebarContent = ({ isMobile, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, selectedGroupMember } = useSelector((state) => state.auth);
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupName, setGroupName] = useState("");

  const getMemberId = (member) =>
    typeof member === "string"
      ? member
      : member?.clientId ||
        member?._id ||
        member?.client_id?._id ||
        member?.client_id;

  useEffect(() => {
    const fetchGroupMembers = async () => {
      try {
        const response = await getApi({
          apiUrl: API_URLS.GET_GROUP_MEMBERS,
        });
        if (response.success && response.data) {
          if (response.data.members) {
            setGroupMembers(
              response.data.members.map((member) => {
                const normalizedId =
                  member?.clientId ||
                  member?._id ||
                  member?.client_id?._id ||
                  member?.client_id;
                return {
                  ...member,
                  clientId: normalizedId,
                  _id: member._id || normalizedId,
                  client_id: member.client_id || normalizedId,
                };
              }),
            );
          }
          if (response.data.groupName) {
            setGroupName(response.data.groupName);
          }
        }
      } catch (error) {
        console.error("Error fetching group members:", error);
      }
    };

    fetchGroupMembers();
  }, []);

  const handleMemberChange = async (value) => {
    if (value === "none") {
      dispatch(setSelectedGroupMember(null));
    } else {
      const member = groupMembers.find((m) => getMemberId(m) === value);
      const resolvedId = value || getMemberId(member);

      dispatch(
        setSelectedGroupMember(
          member
            ? {
                ...member,
                clientId: resolvedId,
                _id: member._id || resolvedId,
                client_id: member.client_id || resolvedId,
              }
            : {
                clientId: resolvedId,
                _id: resolvedId,
                client_id: resolvedId,
                name: "Selected Member",
              },
        ),
      );
    }
  };

  const filteredMenuItems = useMemo(() => {
    let items = MENU_ITEMS;

    if (user?.subAdmin?.isSubAdmin || user?.managementView?.isManagementView) {
      const perms = [
        ...(user?.subAdmin?.permissions || []),
        ...(user?.managementView?.permissions || []),
      ];
      const MANAGEMENT_ALLOWED_KEYS = [
        "dashbaord",
        "dashboard",
        "emission-tracking",
        "environmental-impact",
        "social",
        "governance",
        "supply-chain",
        "sdg",
        "support",
      ];

      const hasAccess = (label) => {
        const moduleKey = MODULE_KEY_MAP[label];
        if (!moduleKey) return true;

        const isManagementView = !!user?.managementView?.isManagementView;
        if (isManagementView && MANAGEMENT_ALLOWED_KEYS.includes(moduleKey)) {
          return true;
        }

        const hasFullDashboardPerm = perms.some(
          (p) =>
            (p.module === "dashboard" || p.module === "dashbaord") &&
            (p.access === "read" || p.access === "write"),
        );
        if (
          hasFullDashboardPerm &&
          MANAGEMENT_ALLOWED_KEYS.includes(moduleKey)
        ) {
          return true;
        }

        return perms.some(
          (p) =>
            p.module === moduleKey &&
            (p.access === "read" || p.access === "write"),
        );
      };

      const filterItems = (menuList) => {
        return menuList
          .map((item) => {
            if (item.children) {
              const filteredChildren = filterItems(item.children);
              if (hasAccess(item.label) || filteredChildren.length > 0) {
                return { ...item, children: filteredChildren };
              }
              return null;
            } else {
              return hasAccess(item.label) ? item : null;
            }
          })
          .filter(Boolean);
      };

      items = filterItems(items);
    }

    if (user?.registeredViaAdmin || user?.client?.registeredViaAdmin) {
      return items.filter((item) => item.label === "Dashboard");
    }

    return items;
  }, [user]);

  // Handle Menu Click
  const handleMenuClick = (e) => {
    navigate(e.key);
    if (isMobile && onClose) {
      onClose();
    }
  };

  // Handle Parent Item Click
  const handleParentClick = (key) => {
    navigate(key);
    if (isMobile && onClose) {
      onClose();
    }
  };

  // Handle Switch to Offset Marketplace ???? will implement in feature
  const handleSwitchToOffsetMarketPlace = async () => {
    try {
      window.open("https://marketplace.offsetgo.earth", "_blank");
      return;
      const response = await postApi({
        apiUrl: API_URLS.CREATE_SWITCH_REQUEST,
        data: { id: "" },
      });
      console.log("response --", response);
      if (response.success) {
        const url = `${process.env.MARKET_PLACE_URL}/switch/${response.data}`;
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error switching to marketplace", error);
    }
  };

  return (
    <div
      className="bg-white h-full flex flex-col justify-between border-r border-slate-100/80"
      style={{ height: "100vh" }}
    >
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Sidebar Logo Header */}
        <div className=" py-3   ">
          <SidebarLogo isMobile={isMobile} />
        </div>

        {/* Scrollable menu container (Scrollbar hidden) */}
        <div className="flex-1 px-3 py-2 overflow-y-auto custom-scrollbar flex flex-col gap-2">
          {/* Dashboard Section */}
          <SidebarMenuCustom
            items={filteredMenuItems.filter((i) => i.label === "Dashboard")}
            location={location}
            onMenuClick={handleMenuClick}
            onParentClick={handleParentClick}
          />

          {/* Premium Minimalist Group View Selection (Indented Text, No noise) */}
          {groupMembers.length > 0 && (
            <div className="flex flex-col gap-0.5">
              {/* Parent Group Header */}
              <div className="flex items-center gap-3 px-3 py-1.5 text-[#000000] cursor-default font-medium">
                <span className="text-[#000000]">
                  <RiOrganizationChart className="w-[17px] h-[17px]" />
                </span>
                <span className="text-[13.5px] leading-none">Group View</span>
              </div>

              {/* Sub items matching standard indentation with Bullet Dots */}
              <div className="flex flex-col gap-0.5">
                {/* Group Members from API */}
                {groupMembers.map((member) => {
                  const memberId = getMemberId(member);
                  const isActive =
                    selectedGroupMember &&
                    getMemberId(selectedGroupMember) === memberId;
                  return (
                    <div
                      key={memberId}
                      onClick={() => handleMemberChange(memberId)}
                      className={`flex items-center gap-2 py-1.5 pl-10 pr-3 rounded-lg cursor-pointer text-[13px] font-medium transition-all duration-150 ease-in-out ${
                        isActive
                          ? "text-[#06513D] font-semibold bg-[#E8F5F125]"
                          : "text-[#000000] hover:text-slate-900 hover:bg-slate-50/50"
                      }`}
                    >
                      <span
                        className={`text-[14px] leading-none ${isActive ? "text-[#06513D]" : "text-[#000000]"}`}
                      >
                        •
                      </span>
                      <span>{member.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Project, Admin & Utility sections */}
          <SidebarMenuCustom
            items={filteredMenuItems.filter((i) => i.label !== "Dashboard")}
            location={location}
            onMenuClick={handleMenuClick}
            onParentClick={handleParentClick}
          />
        </div>
      </div>

      {/* Switch to Marketplace button at bottom - Original Light Blue Card Restored */}
      {!(user?.registeredViaAdmin || user?.client?.registeredViaAdmin) && (
        <div
          className={`flex-shrink-0 flex justify-center items-center py-3 px-3 bg-white border-t border-slate-100/60 text-center ${
            isMobile ? "mb-8" : ""
          }`}
        >
          <div className="bg-[#E5F3F6] rounded-lg p-2 h-24  w-full max-w-[120px] text-center">
            <Typography.Text className="text-[13px] font-medium block">
              Switch to
            </Typography.Text>
            <Typography.Text className="text-[9px] font-light block leading-tight">
              OffsetGo <br></br>MarketPlace
            </Typography.Text>
            <Button
              type="primary"
              onClick={handleSwitchToOffsetMarketPlace}
              className="rounded-md text-[8px] text-white mt-3 h-5 min-w-[80px] hover:!bg-[#387464]"
              style={{
                fontSize: "10px",
                height: "24px",
                padding: "0 4px",
                lineHeight: "1.2",
                backgroundColor: "#06513D",
              }}
            >
              Click here
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Main OrganizationSidebar Component
const OrganizationSidebar = ({ isOpen, onClose, isMobile }) => {
  if (isMobile) {
    return (
      <Drawer
        title={null}
        placement="left"
        onClose={onClose}
        open={isOpen}
        width={268}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ display: "none" }}
        className="mobile-sidebar-drawer"
      >
        <SidebarContent isMobile={true} onClose={onClose} />
      </Drawer>
    );
  }

  return (
    <Sider
      width={258}
      className="bg-white"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        height: "100vh",
        overflow: "hidden",
        zIndex: 1000,
        borderRight: "1px solid #f1f5f9",
      }}
    >
      <SidebarContent isMobile={false} />
    </Sider>
  );
};

export default OrganizationSidebar;
